import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { merge, zip } from 'rxjs';
import { Settings } from '../../../account/models';
import { SettingService } from '../../../account/services';
import { User } from '../../../auth/models';
import { CoverLetter, Document } from '../../../common/models';
import { CoverLetterService, TenderDocumentsService } from '../../../common/services';
import { PlattsPricesService } from '../../../indexes/services';
import { indexRateKey } from '../../../indexes/services/platts/platts-prices.service';
import { ToastService } from '../../../main/services';
import { BidHistoryStruct } from "../../../supplier/models";
import { Contract, ContractSupplier, ROUND_SUPPLIER_STATUSES, Round, Tender } from '../../models';
import { CONTRACT_SUPPLIER_STATUSES } from '../../models/contract-supplier/contract-supplier.statuses';
import { Offer } from "../../models/offer";
import { ROUND_STATUSES } from '../../models/round';
import { AWAITING_OFFER_STATUSES } from '../../models/round-supplier/round-supplier.statuses';
import {
    TenderContractBidsAnalysisService,
    TenderContractSuppliersService,
    TenderContractsService,
    TenderService, TenderSupplierDocumentsService
} from '../../services';

@Component({
    selector: 'jft-tender-contract-dashboard-supplier-awarded',
    templateUrl: './tender-contract-dashboard-supplier-awarded.component.html',
    styleUrls: ['./tender-contract-dashboard-supplier-awarded.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderContractDashboardSupplierAwardedComponent implements OnInit {

    public tender: Tender;
    public contract: Contract;
    public offerStatuses = ROUND_SUPPLIER_STATUSES;
    public awaitingOfferStatuses = AWAITING_OFFER_STATUSES;
    public suppliers: ContractSupplier[] = [];
    public supplier: ContractSupplier;
    public dateNow = new Date();
    public contractSupplierStatuses = CONTRACT_SUPPLIER_STATUSES;
    public companySettings: Settings;
    public loadingAward: boolean = false;
    public roundNum: number;
    public round: any;
    public roundStatuses = ROUND_STATUSES;
    public rounds: Round[] = [];
    public loadingDecline: boolean = false;

    public coverLetter: CoverLetter;
    public documents: Document[] = [];
    public supplierRequestedDocuments: Document[] = [];
    public bidHistory: BidHistoryStruct;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public user: User,
        private settingsService: SettingService,
        protected toast: ToastService,
        private service: TenderContractBidsAnalysisService,
        private supplierService: TenderContractSuppliersService,
        private tenderContractService: TenderContractsService,
        private tenderService: TenderService,
        private documentsService: TenderDocumentsService,
        private supplierDocumentsService: TenderSupplierDocumentsService,
        private coverLetterService: CoverLetterService,
        private plattsPricesService: PlattsPricesService
    ) {
    }

    public ngOnInit() {
        let parentData = this.route.parent.snapshot.data,
            data = this.route.snapshot.data,
            parentParentData = this.route.parent.parent.parent.snapshot.data;

        this.supplier = data['supplier'];
        this.supplier.supplier.users = this.supplier.supplier.users.filter(user => !user.archived);
        this.contract = parentData['contract'];
        this.tender = parentParentData['tender'];
        this.rounds = parentData['rounds'];
        this.coverLetter = data['coverLetter'];
        this.documents = data['documents'];
        this.bidHistory = data['bidHistory'];
        this.roundNum = this.route.snapshot.params.rid;
        this.round = this.rounds.filter(item => item.round == this.roundNum)[0];

        this.settingsService.fetch().subscribe((settings) => {
            this.companySettings = settings;
        });

        this.initSupplierDocuments();
    }

    private initSupplierDocuments() {
        if (this.supplier.supplierRequestedDocument) {
            this.supplier.supplierRequestedDocument.forEach((item) => {
                this.supplierRequestedDocuments.push(Document.create(item.file));
            });
        }
    }

    public award(supplier: ContractSupplier) {
        this.loadingAward = true;
        this.supplierService.award(this.tender, this.contract, supplier).subscribe(() => {
            this.refresh(`Location: ${this.contract.location.getLabel()} awarded`);

        }, (e) => {
            supplier.awardedPercent = null;
            this.toast.error(e);
        });
    }

    private refresh(message: string) {
        zip(
            this.service.fetch(this.tender.id, this.contract.id),
            this.tenderContractService.renew(this.tender, this.contract),
            this.tenderService.renew(this.tender)
        ).subscribe((data) => {
            this.supplier = data[0][0];
            this.contract = data[1];
            this.tender = data[2];
            this.round = this.contract.currentRound;
            this.loadingAward = false;
            this.loadingDecline = false;
            this.toast.success(message);
        });
    }

    public roundChanged(round) {
        this.round = round;
        let url = `/tenders/${this.tender.id}/locations/${this.contract.id}/dashboard/rounds/`
            + `${round.round}/suppliers/${this.supplier.supplier.id}`;
        this.router.navigateByUrl(url);
    }

    public isCurrentRound() {
        return this.supplier.round && this.supplier.round.round == this.contract.currentRound.round;
    }

    public updateDocumentsEvent($emit) {
        // Handle document deletion
        if ($emit && $emit.id) {
            this.deleteDocument($emit);
        } else {
            // Just refresh the documents
            this.updateDocuments();
            this.updateCoverLetter();
            this.updateSupplierDocuments();
        }
    }

    private deleteDocument(document: Document) {
        // Check if it's a cover letter file
        if (this.coverLetter && this.coverLetter.file && this.coverLetter.file.id === document.id) {
            this.coverLetterService.removeDocument(this.tender, this.coverLetter, document).subscribe(() => {
                this.toast.success(`Cover letter file: ${document.name} removed`);
                this.updateCoverLetter();
            }, (e) => {
                this.toast.error(e);
            });
        } else {
            // It's a regular document
            this.documentsService.remove(this.tender, document).subscribe(() => {
                this.toast.success(`Document: ${document.name} removed`);
                this.updateDocuments();
            }, (e) => {
                this.toast.error(e);
            });
        }
    }

    public updateDocuments() {
        this.documentsService.fetch(this.tender.id).subscribe((document: Document[]) => {
            this.documents = document;
        })
    }

    public updateCoverLetter() {
        this.coverLetterService.fetch(this.tender.id).subscribe((document: CoverLetter) => {
            this.coverLetter = document;
        })
    }

    public updateSupplierDocuments() {
        // this.supplierDocumentsService.fetch(this.tender.id).subscribe((document: Document[]) => {
        //     this.supplierRequestedDocuments = document;
        // })
    }

    public addDocumentFile(event: any) {
        this.createDocument(event.target.files)
    }

    public createDocument(files: File[]) {
        let observables = [];

        for (let i = 0; i < files.length; i ++) {
            let file = files[i];
            observables.push(this.documentsService.create(this.tender, file));
            this.toast.success(`Document: ${file.name} has been uploaded`);
        }

        merge(observables).subscribe((results) => {
            results.forEach((document) => {
                this.documents.push(document);
            });
        }, (e) => {
            this.toast.error(e);
        });
    }

    public onDateRange($event) {
        if (!this.supplier.bid.plattsIndexPrice) {
            return null;
        }

        this.getAveragePriceBase($event);
    }

    private getAveragePriceBase(event) {
        let params = {
            'currencyId': this.tender.currencyId,
            'unitId': this.tender.unitsId,
            'startDate': event.dateStart,
            'endDate': event.dateEnd,
            'tenderId': this.tender.id,
            'wToVolume': indexRateKey(this.supplier.bid),
        };

        this.plattsPricesService.fetchPriceAverage(this.supplier.bid.plattsIndexPrice.index.id,
            this.supplier.bid.plattsIndexPrice.bates.id, event.code, params)
            .subscribe((data) => {
                this.supplier.bid.plattsIndexPrice.value = data;
            }, (e) => {
                this.toast.error(e);
            });
    }

    public declineOffer(offer: ContractSupplier) {
        this.loadingDecline = true;
        this.supplierService.decline(this.tender, this.contract, offer.id).subscribe(() => {
            this.refresh(`Location: ${this.contract.location.getLabel()} offer was declined`);
        }, (e) => {
            this.loadingDecline = false;
            this.toast.error(e);
        });

    }

    public cancelAward(offer: any) { //todo refactor
        this.loadingAward = true;

        this.supplierService.cancelAward(this.tender, this.contract, offer).subscribe(() => {
            this.refresh(`Location: ${this.contract.location.getLabel()} has been canceled awarded status`);
        }, (e) => {
            this.loadingAward = false;
            offer.awardedPercent = null;
            this.toast.error(e);
        });
    }

    public onUpdatedBidModal(event, offer: Offer) {
        // Handle new callback structure from bid edit modal
        const offcanvas = event.offcanvas || event;
        const onComplete = event.onComplete;

        this.refresh(`Location: ${this.contract.location.getLabel()} offer has been updated`);
        offcanvas.close('Close modal');
        if (onComplete) onComplete();
    }

    public canShowAdvancedActions(): boolean {
        return !this.tender.isCancelled() && this.user.canManageAdvanced();
    }

    public getAllDocuments(): Document[] {
        const allDocs = [];

        // Add cover letter if exists
        if (this.coverLetter && this.coverLetter.file) {
            allDocs.push(this.coverLetter.file);
        }

        // Add buyer documents
        if (this.documents && this.documents.length) {
            allDocs.push(...this.documents);
        }

        // Add supplier documents
        if (this.supplierRequestedDocuments && this.supplierRequestedDocuments.length) {
            allDocs.push(...this.supplierRequestedDocuments);
        }

        return allDocs;
    }
}
